import React, { useState, useEffect, useCallback } from 'react';
import { TextInputProps } from 'react-native';

import { TypeaheadSearch, TypeaheadOption } from './typeahead-search';

import { dataService } from '@/services/data-service';
import type { Association } from '@/types/models';
import { compareAssociationNames } from '@/utils/association-name';

export type AssociationTypeaheadProps = {
	value: Association | null;
	onSelect: (association: Association | null) => void;
	placeholder?: string;
	label?: string;
	style?: TextInputProps['style'];
	testID?: string;
	autoFocus?: boolean;
	filterAssociations?: (associations: Association[]) => Association[];
	status?: 'active' | 'inactive' | 'all';
	/** Seed the search text (and results) on mount — see TypeaheadSearchProps.initialQuery. */
	initialQuery?: string;
};

export const AssociationTypeahead: React.FC<AssociationTypeaheadProps> = ({
	value,
	onSelect,
	placeholder = 'Search associations...',
	label,
	style,
	testID,
	autoFocus = false,
	filterAssociations,
	status = 'active',
	initialQuery,
}) => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | undefined>();
	const [associations, setAssociations] = useState<Association[]>([]);

	// `filterAssociations` is a PRESENTATION concern, applied at render time
	// below — deliberately NOT a dependency here. Callers pass an inline
	// function (new identity on every render), so depending on it would give
	// `onSearch` a new identity each parent render, refiring the mount effect
	// and firing one /associations search per keystroke in a sibling field (#361).
	const onSearch = useCallback((text: string) => {
		setLoading(true);
		setError(undefined);

		const options: Record<string, string | number> = {
			search: text,
			limit: 50,
		};
		if (status !== 'all') {
			options.status = status;
		}

		dataService.getAssociations(options)
			.then((response) => {
				setAssociations(response.data);
				setLoading(false);
			})
			.catch((err) => {
				console.error('Error searching associations:', err);
				setError('Failed to search associations');
				setLoading(false);
			});
	}, [status]);

	const handleSelect = useCallback((option: TypeaheadOption | null) => {
		if (option) {
			const association = associations.find(a => a.id === option.id);
			if (association) {
				onSelect(association);
			}
		} else {
			onSelect(null);
		}
	}, [associations, onSelect]);

	// Apply the caller's filter here (render time), not in the fetch — keeps the
	// full list in state for handleSelect lookups while the dropdown shows only
	// the permitted subset, and re-applies for free when the filter changes.
	const visibleAssociations = [...(filterAssociations ? filterAssociations(associations) : associations)]
		.sort((a, b) => compareAssociationNames(a.name, b.name));

	const options: TypeaheadOption[] = visibleAssociations.map(association => {
		const descriptionParts = [];

		// Add Property Manager info
		if (association.assignedTo) {
			descriptionParts.push('Assigned');
		} else {
			descriptionParts.push('Unassigned');
		}

		// Add city
		if (association.city) {
			descriptionParts.push(association.city);
		}

		// Add units
		if (association.numberOfUnits) {
			descriptionParts.push(`${association.numberOfUnits} units`);
		}

		return {
			id: association.id,
			label: association.name,
			description: descriptionParts.join(' • '),
		};
	});

	useEffect(() => {
		onSearch(initialQuery ?? '');
		// eslint-disable-next-line react-hooks/exhaustive-deps -- initialQuery is a mount-time seed, not reactive
	}, [onSearch]);

	return (
		<TypeaheadSearch
			selectedOption={value ? {
				id: value.id,
				label: value.name,
				description: (() => {
					const descriptionParts = [];

					// Add Property Manager info
					if (value.assignedTo) {
						descriptionParts.push('Assigned');
					} else {
						descriptionParts.push('Unassigned');
					}

					// Add city
					if (value.city) {
						descriptionParts.push(value.city);
					}

					// Add units
					if (value.numberOfUnits) {
						descriptionParts.push(`${value.numberOfUnits} units`);
					}

					return descriptionParts.join(' • ');
				})(),
			} : null}
			onSearch={onSearch}
			onSelect={handleSelect}
			options={options}
			loading={loading}
			error={error}
			placeholder={placeholder}
			label={label}
			style={style}
			testID={testID}
			autoFocus={autoFocus}
			initialQuery={initialQuery}
			// No association quick creation
			allowCreate={false}
		/>
	);
};
